import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { DocumentTextIcon, CurrencyDollarIcon, TagIcon, ReceiptPercentIcon, PaperClipIcon, XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import { AlertContext } from '../../../context/AlertContext';
import { useHistory } from 'react-router-dom';
import useConstructDatesDisplay from '../../../hooks/useConstructDatesDisplay';
import useFormatterCurrency from '../../../hooks/useFormatterCurrency';
import useFormatDate from '../../../hooks/useFormatDate';
import useScrollPosition from '../../../hooks/useScrollPosition';
import { getAccount } from '../../../apiClient/operations/accountsOperations';
import { getQuoteS3 as downloadFile } from '../../../apiClient/operations/s3UploadOperations';
import PaymentInstruction from '../../../tailwindUI/PaymentInstruction';
import Stats from '../../../tailwindUI/Stats';
import Badge from '../../../tailwindUI/Badge';
import Modal from '../../../tailwindUI/Modal';
import SecondaryLoader from '../../../hooks/SecondaryLoader';
import PrimaryButton from '../../../tailwindUI/PrimaryButton';
import { saveAs } from 'file-saver';
import PermissionsGate from '../../../cognito/permissions/PermissionsGate';
import { ROLES } from '../../../cognito/permissions/permission-maps';

function ContractDetails({ contract }) {

    const history = useHistory();
    const setScrollPosition = useScrollPosition();
    const { formatDate } = useFormatDate();
    const { formatterCurrency } = useFormatterCurrency();
    const { setAlert } = useContext(AlertContext);
    const [stats, setStats] = useState([]);
    const [documentsDownloading, setDocumentsDownloading] = useState([]);
    const [datesDisplay, setDatesDisplay] = useState([]);
    const [paymentInstructions, setPaymentInstructions] = useState([]);
    const [isLoading, setIsloading] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    useEffect(() => {
        if (!contract) return;
        const services = getContractServices(contract.payment_instructions?.flatMap(instruction => instruction.services));
        setPaymentInstructions(contract.payment_instructions.map(instruction => {
            return {
                ...instruction,
                services: services.filter(service => service.instruction_id == instruction.id)
            }
        }));
        setDatesDisplay(getDatesDisplay(services));
        const totalAmount = (contract?.price?.total_amount / 100) || 0;
        const subtotalAmount = (contract?.price?.subtotal_amount / 100) || 0;
        const discountAmount = (contract?.price?.discount_amount / 100) || 0;
        const discountPercentage = contract?.price?.discount_percentage ? `${contract?.price?.discount_percentage?.toFixed(2)}%` : null;
        setStats([
            { id: 1, name: 'Monto total', stat: formatterCurrency(totalAmount), icon: CurrencyDollarIcon },
            { id: 2, name: 'Monto base', stat: formatterCurrency(subtotalAmount), icon: TagIcon },
            { id: 3, name: 'Descuento', stat: formatterCurrency(discountAmount), icon: ReceiptPercentIcon, change: discountPercentage, changeType: 'increase' },
            { id: 4, name: 'Contratación', stat: contract?.price?.price_type == 'special_price' ? 'Paquete' : 'Servicios', icon: DocumentTextIcon }
        ])
    }, [contract]);

    useEffect(() => {
        setScrollPosition(0);
    }, []);

    const getContractServices = (services) => {
        return services?.map((service, i) => {
            const { price } = service;
            return {
                ...service,
                sku: i,
                amount: price.unit_price / 100,
                discount: price.discount,
                discount_amount: price.discount_amount / 100,
                discount_option: price.discount_type,
                subtotal: price.subtotal_amount / 100,
                total: price.total_amount / 100,
                resume_open: false
            }
        })
    }

    const getDatesDisplay = useCallback((services) => {
        return services?.map((service, i) => {
            const { id, name, periods } = service;
            const obj = {
                id: id,
                sku: i,
                index: i,
                name: name,
                dates_display: useConstructDatesDisplay(periods),
            }
            return obj
        });
    }, [contract]);

    const editContract = async () => {
        setShowEditModal(false);
        setIsloading(true);
        try {
            const account = await getAccount(contract?.account?.id);
            const services = contract.payment_instructions?.flatMap(instruction => instruction.services);
            history.push({
                pathname: '../create',
                state: {
                    account: account,
                    createdBy: contract?.created_by,
                    quoteId: contract?.quote_id,
                    contract: contract,
                    serviceItems: {
                        services: getContractServices(services),
                        datesDisplay: getDatesDisplay(services)
                    },
                    priceType: contract?.price?.price_type || 'base_price'
                }
            });
            setScrollPosition(0);
            setIsloading(false);
        } catch (e) {
            console.log("e:", e);
            setIsloading(false);
            setAlert({ active: true, type: 'error', message: 'Error al procesar tu solicitud' });
        }
    }

    const sleep = useCallback((delay) => new Promise(resolve => setTimeout(resolve, delay)));

    const download = async (document) => {
        if (documentsDownloading?.includes(document.key)) return;

        setDocumentsDownloading([
            ...documentsDownloading,
            document.key
        ]);
        try {
            const request = { 'id': document.key, 'bucket': document.bucket };
            let getFile = await downloadFile(request);
            let attemps = 0;
            let retry = getFile.size <= 0;
            while (retry) {
                await sleep(1000);
                getFile = await downloadFile(request);
                attemps += 1;
                retry = getFile.size <= 0 && attemps < 30;
            }

            if (getFile.size > 0) {
                const name = document?.name || document?.key?.split("/")?.pop();
                saveAs(getFile, name);
            } else {
                setAlert({ active: true, type: 'error', message: 'Hubo un problema al obtener el archivo, vuelve a intentarlo' })
            }

            setDocumentsDownloading(prevDocuments => prevDocuments?.filter(doc => doc != document.key));
        } catch (error) {
            console.log(error);
            setAlert({ active: true, type: 'error', message: 'Hubo un problema al obtener el archivo, vuelve a intentarlo' })
            setDocumentsDownloading(prevDocuments => prevDocuments?.filter(doc => doc != document.key));
        }
    }

    if (!contract) return null;

    return (
        <>
            {isLoading && <SecondaryLoader />}
            <div className='space-y-4'>
                <div className="overflow-hidden bg-white shadow rounded-lg border border-gray-200">
                    <div className="px-4 py-5 sm:px-6 flex gap-2">
                        <div>
                            <h3 className="text-lg font-medium leading-6 text-gray-900">Contrato</h3>
                        </div>
                        <Badge text={contract?.status} />
                    </div>
                    <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                        <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                            <div className="sm:col-span-1">
                                <dt className="text-sm font-medium text-gray-500">Folio</dt>
                                <dd className="text-gray-900">
                                    {contract?.folio}
                                </dd>
                            </div>
                            <div className="sm:col-span-1">
                                <dt className="text-sm font-medium text-gray-500">Fecha de creación</dt>
                                <dd className="text-gray-900">
                                    {formatDate(contract?.created, 'DD MMM YYYY, HH:mm:ss')}
                                </dd>
                            </div>
                            {contract?.created_by && (
                                <div className="sm:col-span-1">
                                    <dt className="text-sm font-medium text-gray-500">Creado por</dt>
                                    <dd className="text-gray-900">
                                        {contract?.created_by?.name}
                                    </dd>
                                </div>
                            )}
                            <div className="sm:col-span-1">
                                <dt className="text-sm font-medium text-gray-500">Fecha de activación</dt>
                                <dd className="text-gray-900">
                                    {contract?.activation_date ? formatDate(contract?.activation_date, 'DD MMM YYYY, HH:mm:ss'):"-"}
                                </dd>
                            </div>
                            {contract?.modified_by && (
                                <div className="sm:col-span-1">
                                    <dt className="text-sm font-medium text-gray-500">Modificado por</dt>
                                    <dd className="text-gray-900">
                                        {contract?.modified_by?.name}
                                    </dd>
                                </div>
                            )}
                            {contract?.document && (
                                <div className="sm:col-span-2">
                                    <dt className="text-sm font-medium text-gray-500">Documentos</dt>
                                    <dd className="mt-1 text-sm text-gray-900">
                                        <ul role="list" className="divide-y divide-gray-200 rounded-md border border-gray-200">
                                            {contract?.document?.map((document, i) =>
                                                <li key={i} className="flex items-center justify-between py-5 md:py-3 pl-3 pr-4 text-sm">
                                                    <div className="flex w-0 flex-1 items-center">
                                                        <PaperClipIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                                                        <span className="md:hidden ml-2 w-0 flex-1 truncate">
                                                            {document?.name || document?.key?.split("/")?.pop()}
                                                        </span>
                                                        <span className="hidden md:flex ml-2 truncate cursor-pointer" onClick={() => { download(document) }}>
                                                            {document?.name || document?.key?.split("/")?.pop()}
                                                        </span>
                                                    </div>
                                                    <div className="md:hidden ml-4 flex-shrink-0 flex gap-2">
                                                        {documentsDownloading?.includes(document.key) ?
                                                            <span className="flex items-center">
                                                                <svg className="inline w-7 h-7 text-v2-blue-text-login animate-spin fill-white" viewBox="0 0 100 101" fill="current" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                                                                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                                                                </svg>
                                                            </span>
                                                            :
                                                            <ArrowDownTrayIcon
                                                                className='w-7 h-7 cursor-pointer text-v2-blue-text-login'
                                                                onClick={() => { download(document) }} />
                                                        }
                                                    </div>
                                                    <div className="hidden md:flex ml-4 flex-shrink-0 gap-2">
                                                        {documentsDownloading?.includes(document.key) &&
                                                            <span className="flex items-center">
                                                                <svg className="inline w-5 h-5 text-v2-blue-text-login animate-spin fill-white" viewBox="0 0 100 101" fill="current" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                                                                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                                                                </svg>
                                                            </span>
                                                        }
                                                        <button className={`${documentsDownloading?.includes(document.key) ? 'cursor-default text-gray-400' : 'text-v2-blue-text-login'}  transition-all font-medium`}
                                                            onClick={() => { download(document) }}>
                                                            Descargar
                                                        </button>
                                                    </div>
                                                </li>
                                            )}
                                        </ul>
                                    </dd>
                                </div>
                            )}
                        </dl>
                    </div>
                </div>
                <Stats items={stats} />
                <div>
                    <PaymentInstruction
                        data={paymentInstructions}
                        setData={setPaymentInstructions}
                        servicesDisplay={datesDisplay}
                        viewOnly />
                </div>

                {contract?.status == 'pending' && (
                    <PermissionsGate allowedRoles={[ROLES.root, ROLES.help_desk, ROLES.administrators, ROLES.sales, ROLES.collections, ROLES.operationsAtc]}>
                        <div className='flex justify-end pb-12 md:pb-4'>
                            <div className='w-full md:w-1/3'>
                                <PrimaryButton isFullWidth onClick={() => setShowEditModal(true)}>
                                    Editar acuerdos de pago
                                </PrimaryButton>
                            </div>
                        </div>
                    </PermissionsGate>
                )}
            </div>
            <Modal show={showEditModal} setShow={setShowEditModal} className='min-w-full sm:min-w-[500px]'>
                <div className="py-2 px-2 text-center">
                    <div className='w-full text-gray-400 flex justify-end'><XMarkIcon className='w-7 h-7 cursor-pointer' onClick={() => setShowEditModal(false)} /></div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">Editar acuerdos de pago</h3>
                    <div className="mt-2 max-w-xl text-sm text-gray-500">
                        <p>¿Estás seguro que deseas editar los acuerdos de pago?</p>
                    </div>
                    <div className="mt-6">
                        <div className='w-full md:w-3/5 mx-auto text-center'>
                            <PrimaryButton isFullWidth={true} onClick={editContract}>
                                Sí, editar acuerdos de pago
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    )
}

ContractDetails.propTypes = {
    contract: PropTypes.object
}

export default ContractDetails;